import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Grid2,
} from "@mui/material";
import { useTranslationHook } from "../../hooks";
import { EmailLog, EmailLogsRequest } from "../../types/model/email";
import { formatDate } from "../../utils/formatDate";
import { Account } from "../../types/model/account";
import { Template } from "../../api/email/getTemplates";

interface EmailLogTableProps {
  logs: EmailLog[];
  accounts: Account[];
  templates: Template[];
  filters: EmailLogsRequest;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (e: SelectChangeEvent<string>) => void;
}

const EmailLogTable: React.FC<EmailLogTableProps> = ({
  logs,
  accounts,
  templates,
  filters,
  onInputChange,
  onSelectChange,
}) => {
  const { t } = useTranslationHook();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (log: EmailLog) => {
    setSelectedLog(log);
  };

  const handleCloseDialog = () => {
    setSelectedLog(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t("admin.email-logs.title")}
        </Typography>

        <Grid2 container spacing={3} sx={{ mb: 2 }}>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              name="recipientEmail"
              label={t("admin.email-logs.recipientEmail")}
              variant="outlined"
              value={filters.recipientEmail || ''}
              onChange={onInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t("admin.email-logs.accountId")}</InputLabel>
              <Select
                name="accountId"
                label={t("admin.email-logs.accountId")}
                value={filters.accountId ? String(filters.accountId) : ''}
                onChange={onSelectChange}
              >
                <MenuItem value="">
                  <em>{t("admin.email-logs.all-accounts")}</em>
                </MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t("admin.email-logs.template")}</InputLabel>
              <Select
                name="templateName"
                label={t("admin.email-logs.template")}
                value={filters.templateName || ''}
                onChange={onSelectChange}
              >
                <MenuItem value="">
                  <em>{t("admin.email-logs.allTemplates")}</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.name} value={template.name}>
                    {t(template.i18nTag)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t("admin.email-logs.status")}</InputLabel>
              <Select
                name="status"
                label={t("admin.email-logs.status")}
                value={filters.status || ''}
                onChange={onSelectChange}
              >
                <MenuItem value="">
                  <em>{t("admin.email-logs.none")}</em>
                </MenuItem>
                <MenuItem value="SENT">{t("admin.email-logs.sent")}</MenuItem>
                <MenuItem value="FAILED">{t("admin.email-logs.failed")}</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t("admin.email-logs.recipient")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.email-logs.accountId")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.email-logs.template")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.email-logs.status")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.email-logs.sentAt")}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log) => (
                  <TableRow key={log.id} onClick={() => handleRowClick(log)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>{log.recipientEmail}</TableCell>
                    <TableCell>{accounts.find(acc => acc.id === log.accountId)?.name || log.accountId}</TableCell>
                    <TableCell>{t(templates.find(t => t.name === log.templateName)?.i18nTag || log.templateName)}</TableCell>
                    <TableCell>{t(`admin.email-logs.${log.status.toLowerCase()}`)}</TableCell>
                    <TableCell>{formatDate(log.sentAt)}</TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper sx={{ mt: 2, p: 1 }}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>
      <Dialog open={selectedLog !== null} onClose={handleCloseDialog}>
        <DialogTitle>{t("admin.email-logs.messageId")}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ wordBreak: 'break-all' }}>{selectedLog?.messageId}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("generic.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailLogTable;


